import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useTimer } from "./useTimer";
import { toast } from "@/utils/toast.utils";
import { useAuth } from "@/contexts/AuthContext";
import { getApiErrorMessage } from "@/utils/api-error.utils";
import { createEmailSchema, createOtpSchema } from "@/common/validation.common";
import { OTP_CONFIG } from "@/consts/otp.consts";
import {
  useRequestEmailOtpMutation,
  useVerifyEmailOtpMutation,
} from "@/stores/queries/auth.query";
import { captureException, sendCountMetric, SentryMetric } from "@/utils/sentry";

export enum FlowStep {
  EMAIL = "email",
  OTP = "otp",
}

export const useEmailOtpFlow = () => {
  const { t } = useTranslation();
  const { signInWithOtp } = useAuth();
  const [step, setStep] = useState<FlowStep>(FlowStep.EMAIL);
  const [email, setEmail] = useState("");
  const [merchantUserOtpId, setMerchantUserOtpId] = useState("");

  const { isCompleted, resetTimer, formattedTime } = useTimer({
    initialTime: OTP_CONFIG.TIMER_DURATION,
  });

  const [isSigningIn, setIsSigningIn] = useState(false);

  const { mutate: requestOtp, isPending: isRequestingOtp } =
    useRequestEmailOtpMutation();
  const { mutate: verifyOtp, isPending: isVerifyingOtp } =
    useVerifyEmailOtpMutation();

  // Email form
  const emailSchema = createEmailSchema(t);
  type EmailFormValues = z.infer<typeof emailSchema>;

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
    mode: "onBlur",
  });

  // OTP form
  const otpSchema = createOtpSchema(t);
  type OtpFormValues = z.infer<typeof otpSchema>;

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: "" },
    mode: "onBlur",
  });

  // Handle email submission
  const handleEmailSubmit = (values: EmailFormValues) => {
    requestOtp(
      { email: values.email },
      {
        onSuccess: (response) => {
          setEmail(values.email);
          setMerchantUserOtpId(response.merchantUserOtpId);
          setStep(FlowStep.OTP);
          resetTimer();
          toast.success(t("auth.codeSentSuccess"));
          sendCountMetric(SentryMetric.SEND_VERIFICATION_CODE_SUCCESS);
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error, t("errors.generic")));
          sendCountMetric(SentryMetric.SEND_VERIFICATION_CODE_FAILURE);
        },
      }
    );
  };

  // Handle OTP verification
  const handleOtpSubmit = (values: OtpFormValues) => {
    verifyOtp(
      {
        merchantUserOtpId,
        otpCode: values.otp,
        email,
      },
      {
        onSuccess: async (response) => {
          setIsSigningIn(true);
          try {
            await signInWithOtp(response.customToken);
            sendCountMetric(SentryMetric.VERIFY_VERIFICATION_CODE_SUCCESS);
          } catch (error) {
            sendCountMetric(SentryMetric.VERIFY_VERIFICATION_CODE_FAILURE);
            console.error("Sign in error:", error);
            captureException(error as Error, {
              extra: {
                component: 'useEmailOtpFlow',
                action: 'handleOtpSubmit',
              },
            });
            toast.error(t("auth.signInError"));
            setIsSigningIn(false);
          }
        },
        onError: (error) => {
          sendCountMetric(SentryMetric.VERIFY_VERIFICATION_CODE_FAILURE);
          const errorMessage = getApiErrorMessage(error, t("auth.invalidCode"));
          otpForm.setValue("otp", "");
          otpForm.setError("otp", { message: errorMessage });
        },
      }
    );
  };

  // Resend OTP
  const handleResend = () => {
    requestOtp(
      { email },
      {
        onSuccess: (response) => {
          setMerchantUserOtpId(response.merchantUserOtpId);
          resetTimer();
          toast.success(t("auth.codeResendSuccess"));
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error, t("errors.generic")));
        },
      }
    );
  };

  // Handle back to email step
  const handleBack = () => {
    setStep(FlowStep.EMAIL);
    otpForm.reset();
  };

  return {
    step,
    email,
    emailForm,
    otpForm,
    isRequestingOtp,
    isVerifyingOtp,
    isSigningIn,
    isCompleted,
    formattedTime,
    handleEmailSubmit,
    handleOtpSubmit,
    handleResend,
    handleBack,
  };
};

